import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { rutValido, formatRut } from "../lib/format";
import { Field } from "../components/Field";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const INIT = {
  rut: "", razon_social: "", nombre_fantasia: "", giro: "", direccion: "", comuna: "", telefono: "", correo: "",
  rep_nombre: "", rep_run: "", rep_correo: "", ofi_nombre: "", ofi_run: "", ofi_correo: "", ofi_cargo: "",
  estado_uaf: "", password: "", password2: "",
};

const Section = ({ n, title, children }) => (
  <section className="space-y-4">
    <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-3">
      <span className="font-mono text-xs bg-slate-900 text-white rounded px-2 py-1">{n}</span>{title}
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </section>
);

const rutErr = (v) => (v && !rutValido(v) ? "RUT inválido (revisa el dígito verificador)" : null);

const LegalDoc = ({ doc, checked, onChange }) => (
  <div className="space-y-2">
    <p className="text-sm font-semibold text-slate-900">{doc.titulo} <span className="font-mono text-xs text-slate-500">v{doc.version}</span></p>
    <pre className="whitespace-pre-wrap text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 max-h-40 overflow-y-auto font-sans" data-testid={`legal-text-${doc.documento}`}>{doc.texto}</pre>
    <div className="flex items-center gap-2">
      <Checkbox id={doc.documento} checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={`accept-${doc.documento}-checkbox`} />
      <Label htmlFor={doc.documento} className="text-sm">Acepto el documento "{doc.titulo}"</Label>
    </div>
  </div>
);

const Done = ({ res }) => (
  <div className="text-center space-y-4 py-6" data-testid="register-success">
    <CheckCircle2 className="mx-auto text-emerald-600" size={48} />
    <h1 className="font-heading text-2xl font-bold">Registro recibido</h1>
    <p className="text-slate-600 text-sm max-w-md mx-auto">
      La empresa con RUT <b>{res.rut}</b> quedó en estado <b>pendiente de verificación</b>. El link KYC se habilitará cuando IntiCheck la active.
      Ya puedes ingresar con <b>{res.correo_owner}</b> para configurar tu segundo factor.
    </p>
    <Link to="/ingresar"><Button className="bg-slate-900 hover:bg-slate-800" data-testid="register-go-login">Ir a ingresar</Button></Link>
  </div>
);

export default function Registro() {
  const [f, setF] = useState(INIT);
  const [docs, setDocs] = useState([]);
  const [acc, setAcc] = useState({});
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get("/public/legal").then((r) => setDocs(r.data.documentos));
  }, []);

  const bind = (k) => ({ id: `reg-${k.replace(/_/g, "-")}`, value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  const bindRut = (k) => ({ ...bind(k), onBlur: () => f[k] && setF({ ...f, [k]: formatRut(f[k]) }), error: rutErr(f[k]) });
  const ver = (d) => docs.find((x) => x.documento === d)?.version;

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.password2) return toast.error("Las contraseñas no coinciden");
    setBusy(true);
    try {
      const { data } = await api.post("/public/companies/register", {
        rut: f.rut, razon_social: f.razon_social, nombre_fantasia: f.nombre_fantasia, giro: f.giro, direccion: f.direccion,
        comuna: f.comuna, telefono: f.telefono, correo: f.correo, estado_uaf: f.estado_uaf, password: f.password,
        representante_legal: { nombre: f.rep_nombre, run: f.rep_run, correo: f.rep_correo },
        oficial_cumplimiento: { nombre: f.ofi_nombre, run: f.ofi_run, correo: f.ofi_correo, cargo: f.ofi_cargo },
        acepta_terminos: !!acc.terminos_uso, acepta_contrato: !!acc.contrato_encargo_datos,
        version_terminos: ver("terminos_uso"), version_contrato: ver("contrato_encargo_datos"),
      });
      setRes(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 flex items-start justify-center p-4 sm:p-10">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-6 sm:p-10 space-y-8 text-slate-900">
        <Link to="/" className="flex items-center"><BrandLogo className="h-8" /></Link>
        {res ? <Done res={res} /> : (
          <form onSubmit={submit} className="space-y-10" data-testid="register-form">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Registro de empresa</p>
              <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Registra tu remesadora</h1>
            </div>
            <Section n="01" title="Identificación de la empresa">
              <Field label="RUT empresa" placeholder="76.123.456-K" required {...bindRut("rut")} />
              <Field label="Razón social" required {...bind("razon_social")} />
              <Field label="Nombre de fantasía" required {...bind("nombre_fantasia")} />
              <Field label="Giro" required {...bind("giro")} />
              <Field label="Dirección" required {...bind("direccion")} />
              <Field label="Comuna" required {...bind("comuna")} />
              <Field label="Teléfono" placeholder="+56 9 1234 5678" required {...bind("telefono")} />
              <Field label="Correo de la empresa" type="email" required {...bind("correo")} />
            </Section>
            <Section n="02" title="Representante legal (será el dueño de la cuenta)">
              <Field label="Nombre completo" required {...bind("rep_nombre")} />
              <Field label="RUN" placeholder="12.345.678-5" required {...bindRut("rep_run")} />
              <Field label="Correo (para ingresar)" type="email" required {...bind("rep_correo")} className="sm:col-span-2" />
              <Field label="Contraseña" type="password" required minLength={12} hint="Mínimo 12 caracteres" {...bind("password")} />
              <Field label="Repetir contraseña" type="password" required {...bind("password2")} />
            </Section>
            <Section n="03" title="Oficial de cumplimiento">
              <Field label="Nombre completo" required {...bind("ofi_nombre")} />
              <Field label="RUN" required {...bindRut("ofi_run")} />
              <Field label="Correo" type="email" required {...bind("ofi_correo")} />
              <Field label="Cargo" required {...bind("ofi_cargo")} />
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Estado ante la UAF</Label>
                <Select value={f.estado_uaf} onValueChange={(v) => setF({ ...f, estado_uaf: v })}>
                  <SelectTrigger data-testid="reg-estado-uaf-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inscrita">Inscrita</SelectItem>
                    <SelectItem value="solicitud_en_tramite">Solicitud en trámite</SelectItem>
                    <SelectItem value="no_iniciada">No iniciada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Section>
            <section className="space-y-5">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-3"><span className="font-mono text-xs bg-slate-900 text-white rounded px-2 py-1">04</span>Aceptación legal</h2>
              {docs.map((d) => <LegalDoc key={d.documento} doc={d} checked={!!acc[d.documento]} onChange={(v) => setAcc({ ...acc, [d.documento]: v })} />)}
            </section>
            <Button type="submit" size="lg" disabled={busy || !f.estado_uaf} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="register-submit-button">
              {busy ? "Enviando…" : "Registrar empresa"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
